"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/**
 * Vector math export functions.
 */
var vec;
(function (vec) {
    function add(a, b) {
        if (Array.isArray(b)) {
            if (Array.isArray(a)) {
                if (a.length !== b.length) {
                    throw new Error('Vectors do not have equal dimensions.');
                }
                return a.map((element, i) => element + b[i]);
            }
            return b.map(element => a + element);
        }
        if (Array.isArray(a)) {
            return a.map(element => element + b);
        }
        return a + b;
    }
    vec.add = add;
    function subtract(a, b) {
        if (Array.isArray(b)) {
            if (Array.isArray(a)) {
                if (a.length !== b.length) {
                    throw new Error('Vectors do not have equal dimensions.');
                }
                return a.map((element, i) => element - b[i]);
            }
            return b.map(element => a - element);
        }
        if (Array.isArray(a)) {
            return a.map(element => element - b);
        }
        return a - b;
    }
    vec.subtract = subtract;
    function multiply(a, b) {
        if (Array.isArray(b)) {
            if (Array.isArray(a)) {
                if (a.length !== b.length) {
                    throw new Error('Vectors do not have equal dimensions.');
                }
                return a.map((element, i) => element * b[i]);
            }
            return b.map(element => a * element);
        }
        if (Array.isArray(a)) {
            return a.map(element => element * b);
        }
        return a * b;
    }
    vec.multiply = multiply;
    function divide(a, b) {
        if (Array.isArray(b)) {
            if (Array.isArray(a)) {
                if (a.length !== b.length) {
                    throw new Error('Vectors do not have equal dimensions.');
                }
                return b.map((element, i) => {
                    if (element === 0) {
                        throw new Error('Cannot divide by zero.');
                    }
                    return a[i] / element;
                });
            }
            throw new Error('Cannot divide a scalar by a vector.');
        }
        if (b === 0) {
            throw new Error('Cannot divide by zero.');
        }
        if (Array.isArray(a)) {
            return a.map(element => element / b);
        }
        return a / b;
    }
    vec.divide = divide;
    /**
     * Returns the dot product of two vectors.
     * @param {Vector} v1 A vector.
     * @param {Vector} v2 A vector.
     * @returns {number} The dot product of the input vectors.
     */
    function dot(v1, v2) {
        if (v1.length !== v2.length) {
            throw new Error('Vectors do not have equal dimensions.');
        }
        if (v1.length === 0) {
            throw new Error('Operation is invalid for zero-dimensional vectors.');
        }
        return v1.reduce((prevVal, element, i) => prevVal + (element * v2[i]), 0);
    }
    vec.dot = dot;
    /**
     * Returns the wedge product of two 2D vectors.
     * @param {Vector2} a A 2D vector.
     * @param {Vector2} b A 2D vector.
     * @returns {number} The wedge product of the input vectors.
     */
    function wedge(a, b) {
        if (a.length !== b.length) {
            throw new Error('Vectors do not have equal dimensions.');
        }
        if (a.length !== 2) {
            throw new Error('Vectors must be 2D.');
        }
        return a[0] * b[1] - a[1] * b[0];
    }
    vec.wedge = wedge;
    /**
     * Returns the cross product of two 3D vectors.
     * @param {Vector3} v1 A 3D vector.
     * @param {Vector3} v2 A 3D vector.
     * @returns {Vector3} The cross product of the input vectors.
     */
    function cross(v1, v2) {
        if (v1.length !== v2.length) {
            throw new Error('Vectors do not have equal dimensions.');
        }
        if (v1.length !== 3) {
            throw new Error('Vectors must be 3D.');
        }
        const v1x = v1[0];
        const v1y = v1[1];
        const v1z = v1[2];
        const v2x = v2[0];
        const v2y = v2[1];
        const v2z = v2[2];
        return [(v1y * v2z) - (v1z * v2y), (v1z * v2x) - (v1x * v2z), (v1x * v2y) - (v1y * v2x)];
    }
    vec.cross = cross;
    /**
     * Returns the angle (in radians, between -π and π) between the given vector and the
     * positive x axis.
     * @param {Vector2} v A 2D vector.
     * @returns {number} The angle between the vector and the positive x axis.
     */
    function getAngle(v) {
        if (v.length !== 2) {
            throw new Error('Vector must be 2D.');
        }
        return Math.atan2(v[1], v[0]);
    }
    vec.getAngle = getAngle;
    /**
     * Returns the angle between two vectors (in radians, between 0 and π).
     * @param {Vector} v1 A vector.
     * @param {Vector} v2 A vector.
     * @returns {number} The angle between v1 and v2.
     */
    function angleBetween(v1, v2) {
        // Note that we clamp the dot product between -1 and 1, otherwise numerical imprecision
        // will cause the inverse cosine to return NaN.
        const v1Normalized = vec.normalize(v1);
        const v2Normalized = vec.normalize(v2);
        const dotProduct = vec.dot(v1Normalized, v2Normalized);
        const clampedDotProduct = Math.max(-1, Math.min(dotProduct, 1));
        return Math.acos(clampedDotProduct);
    }
    vec.angleBetween = angleBetween;
    // TODO: write tests
    /**
     * Returns the cotangent of the angle between two 3D vectors.
     * @param {Vector3} v1 A 3D vector.
     * @param {Vector3} v2 A 3D vector.
     * @returns {number} The cotangent of the angle between the input vectors.
     */
    function cotangent(v1, v2) {
        // The cotangent of the angle between two 3D vectors is given by:
        // cot(theta) = cos(theta) / sin(theta) = (v1 . v2) / ||v1 x v2||
        return vec.dot(v1, v2) / vec.length(vec.cross(v1, v2));
    }
    vec.cotangent = cotangent;
    /**
     * Returns a vector computed by rotating the given 2D vector about the origin by the specified
     * angle.
     * @param {Vector2} v A 2D vector.
     * @param {number} angle An angle (in radians).
     * @returns {Vector2} The rotated 2D vector.
     */
    // TODO: rename this to something more specific.
    function rotate(v, angle) {
        if (v.length !== 2) {
            throw new Error('Vector must be 2D.');
        }
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return [cos * v[0] - sin * v[1], sin * v[0] + cos * v[1]];
    }
    vec.rotate = rotate;
    /**
     * Returns a vector computed by rotating the given 3D vector about an axis by the specified
     * angle.
     * @param {Vector3} v A 3D vector.
     * @param {Vector3} axis The normalized 3D vector representing the axis of rotation.
     * @param {number} angle An angle (in radians).
     */
    function rotate3DAxisAngle(v, axis, angle) {
        if (v.length !== 3) {
            throw new Error('Vector must be 3D.');
        }
        if (axis.length !== 3) {
            throw new Error('Axis must be 3D.');
        }
        // http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm
        // Assumes axis is normalized.
        // Calculate quaternion.
        const halfAngle = angle / 2;
        const sin = Math.sin(halfAngle);
        const qx = axis[0] * sin;
        const qy = axis[1] * sin;
        const qz = axis[2] * sin;
        const qw = Math.cos(halfAngle);
        // Calculate quat * vector.
        const ix = qw * v[0] + qy * v[2] - qz * v[1];
        const iy = qw * v[1] + qz * v[0] - qx * v[2];
        const iz = qw * v[2] + qx * v[1] - qy * v[0];
        const iw = -qx * v[0] - qy * v[1] - qz * v[2];
        // Calculate result * inverse quat.
        return [
            ix * qw + iw * -qx + iy * -qz - iz * -qy,
            iy * qw + iw * -qy + iz * -qx - ix * -qz,
            iz * qw + iw * -qz + ix * -qy - iy * -qx,
        ];
    }
    vec.rotate3DAxisAngle = rotate3DAxisAngle;
    /**
     * Returns a vector computed by transforming the given 3D vector by the specified transform.
     * @param {Vector3} v A 3D vector.
     * @param {Matrix4} t The 4x4 transformation matrix.
     */
    function applyTransform(v, t) {
        const t00 = t[0][0];
        const t01 = t[0][1];
        const t02 = t[0][2];
        const t03 = t[0][3];
        const t10 = t[1][0];
        const t11 = t[1][1];
        const t12 = t[1][2];
        const t13 = t[1][3];
        const t20 = t[2][0];
        const t21 = t[2][1];
        const t22 = t[2][2];
        const t23 = t[2][3];
        const t30 = t[3][0];
        const t31 = t[3][1];
        const t32 = t[3][2];
        const t33 = t[3][3];
        const x = v[0];
        const y = v[1];
        const z = v[2];
        const w = 1 / (t30 * x + t31 * y + t32 * z + t33);
        return [
            (t00 * x + t01 * y + t02 * z + t03) * w,
            (t10 * x + t11 * y + t12 * z + t13) * w,
            (t20 * x + t21 * y + t22 * z + t23) * w,
        ];
    }
    vec.applyTransform = applyTransform;
    /**
     * Returns the squared length of a vector.
     * @param {Vector} v A vector.
     * @returns {number} The squared length of the input vector.
     */
    function lengthSquared(v) {
        return vec.dot(v, v);
    }
    vec.lengthSquared = lengthSquared;
    /**
     * Returns the length (the two norm) of a vector.
     * @param {Vector} v A vector.
     * @returns {number} The length of the input vector.
     */
    function length(v) {
        return Math.sqrt(vec.lengthSquared(v));
    }
    vec.length = length;
    function normalize(v) {
        return vec.divide(v, vec.length(v));
    }
    vec.normalize = normalize;
    /**
     * Returns the distance between two vectors (the two-norm of their difference).
     * @param {Vector} v1 A vector.
     * @param {Vector} v2 A vector.
     * @returns {number} The distance between the input vectors.
     */
    function distance(v1, v2) {
        return vec.length(vec.subtract(v1, v2));
    }
    vec.distance = distance;
    function min(a, b) {
        if (Array.isArray(b)) {
            if (Array.isArray(a)) {
                if (a.length !== b.length) {
                    throw new Error('Vectors do not have equal dimensions.');
                }
                return a.map((element, i) => Math.min(element, b[i]));
            }
            return b.map(element => Math.min(element, a));
        }
        if (Array.isArray(a)) {
            return a.map(element => Math.min(element, b));
        }
        return Math.min(a, b);
    }
    vec.min = min;
    function max(a, b) {
        if (Array.isArray(b)) {
            if (Array.isArray(a)) {
                if (a.length !== b.length) {
                    throw new Error('Vectors do not have equal dimensions.');
                }
                return a.map((element, i) => Math.max(element, b[i]));
            }
            return b.map(element => Math.max(element, a));
        }
        if (Array.isArray(a)) {
            return a.map(element => Math.max(element, b));
        }
        return Math.max(a, b);
    }
    vec.max = max;
    /**
     * Returns a vector of the specified dimension, with all components set to the given value.
     * @param {number} value The value to use for each component.
     * @param {number} dim The dimension of the desired vector.
     * @returns {Vector} A constant vector of the specified dimension.
     */
    function fromConstant(value, dim) {
        const v = [];
        for (let i = 0; i < dim; i++) {
            v.push(value);
        }
        return v;
    }
    vec.fromConstant = fromConstant;
    /**
     * Compares two vectors.
     * @param {Vector} v1 A vector.
     * @param {Vector} v2 A vector.
     * @returns {boolean} True if the two input vectors have equal dimensions and equal components;
     * false, otherwise.
     */
    function equal(v1, v2) {
        if (v1.length !== v2.length) {
            return false;
        }
        for (let i = 0; i < v1.length; i++) {
            if (v1[i] !== v2[i]) {
                return false;
            }
        }
        return true;
    }
    vec.equal = equal;
})(vec = exports.vec || (exports.vec = {}));
//# sourceMappingURL=vec.js.map